import { v4 as uuidv4 } from 'uuid';

import { CsRecipe, CsAppliesTo } from '../../../../common/models/content-studio.model';
import { coerceRecipeInputs, CoercionReport, surfaceShotName } from '../recipe-schema';
import {
    CanvasEditImageNode,
    CanvasGenerationNode,
    CanvasNode,
    CanvasProductNode,
    CanvasRecipeNode,
    collectNodeAndDescendants,
} from '../canvas/canvas-graph.types';
import { ProductNodeProduct } from '../canvas/ProductNode';

export type PlanOperationType =
    | 'add_recipe' | 'update_recipe' | 'delete_node' | 'clear_canvas' | 'generate_node'
    | 'retry_generation' | 'approve_generation' | 'add_edit_node' | 'update_edit_node'
    | 'add_video_story';

/** One operation of an agent-proposed plan. Mirrors the API's plan contract. */
export interface PlanOperation {
    op: PlanOperationType;
    id?: string;
    /** add_edit_node + expand: the shape to extend the image out to. */
    aspectRatio?: string;
    /** add_edit_node + design: the text to lay over the picture, and where. */
    design?: DesignSpec;
    /** add_edit_node + retouch: areas to fix, as fractions of the frame. */
    regions?: Array<{ x: number; y: number; width: number; height: number; prompt: string }>;
    /**
     * add_edit_node / update_edit_node + modify: a picture the change should
     * take its target from — "the same chair as in this image". Rides to the
     * worker as inputs.referenceUrl, image 2 beside the one being edited.
     */
    referenceImageUrl?: string;
    /** add_edit_node + modify: what the whole picture should become. */
    prompt?: string;
    /** update_edit_node: which edit node to change. */
    editNodeId?: string;
    recipeSlug?: string;
    productUuid?: string;
    sourceOpId?: string;
    targetNodeId?: string;
    generationNodeId?: string;
    approved?: boolean;
    tool?: string;
    inputs?: Record<string, any>;
    count?: number;
    rationale?: string;
    /** add_video_story: what the video is about, in the vendor's words. */
    brief?: string;
    /** add_video_story: the products it features. */
    productUuids?: string[];
    /** add_video_story: target length in seconds. */
    durationSec?: number;
}

/**
 * A video story the plan asked for: brief → storyboard → shots → clips →
 * timeline. Nothing about it exists on the canvas until the storyboard is
 * written, so it compiles to a request rather than to nodes.
 */
export interface CompiledVideoStory {
    step: PlanOperation;
    brief: string;
    productUuids: string[];
    /** Names, for the plan card. */
    productNames: string[];
    aspect: string;
    durationSec: number;
}

export interface ContentPlan {
    summary: string;
    productUuids: string[];
    operations: PlanOperation[];
    /** What to come back and do once this plan's images have finished. */
    continuation?: { instruction: string };
}

/** A product as the compiler needs it — canvas fields plus what drives validation. */
export interface PlannerProduct extends ProductNodeProduct {
    visualizationType?: CsAppliesTo;
    applicationTypes?: string[];
}

export interface CompiledStep {
    /** The plan operation this came from, for the preview. */
    step: PlanOperation;
    /** The recipe node that will be created. */
    node: CanvasRecipeNode;
    recipe: CsRecipe;
    product?: PlannerProduct;
    /** What had to be changed to make the model's inputs valid. */
    coercion: CoercionReport;
    /** Required inputs still missing — the node spawns, but can't generate yet. */
    missing: string[];
}

/** An existing recipe node the plan wants to re-configure. */
export interface ExistingNode {
    nodeId: string;
    recipeSlug: string;
    /** Human name, so the agent's context reads like the canvas does. */
    recipeName?: string;
    inputs: Record<string, any>;
    /** Which mode the node resolves to — rarely stored in `inputs`. */
    mode?: string;
    /** Stored values the current mode ignores. Editing one changes nothing. */
    ignoredInputs?: string[];
    /** Required fields still empty, so the node cannot run yet. */
    missing?: string[];
    /** True once the node has produced a generation. */
    hasOutput?: boolean;
    /**
     * The 3D studio frame this node was posed for, once one has been captured.
     *
     * Without it a run ends the moment it asks for an angle: the agent sets
     * `cameraAngle`, the studio captures, and the next turn cannot tell whether
     * anything happened — so "now put it in a bathroom" had nothing to build on.
     */
    snapshot?: {
        url: string;
        /** The named angle it was posed to, when the plan asked for one. */
        angle?: string;
        /** How the camera looks at the piece, in words. */
        view?: string;
    };
    product?: PlannerProduct;
}

/**
 * A generated image or video sitting on the canvas.
 *
 * The agent could not see these at all, so "accept both generations" was
 * reinterpreted as a request to generate three more, and "retry the failed
 * one" ran the recipe again and appended a second generation beside it.
 */
export interface CanvasGenerationInfo {
    nodeId: string;
    assetUuid: string;
    /** pending while it runs, ready when it lands, failed when it does not. */
    status?: string;
    /** Whether the vendor has accepted it. Undefined means undecided. */
    approved?: boolean;
    /** The recipe or edit node that produced it. */
    fromNodeId: string;
    /** What it is, in the words the canvas uses. */
    label: string;
    /** Where the finished image is. Present once it is ready. */
    url?: string;
    thumbnailUrl?: string;
    /** Why it failed, when it did — so a retry can be argued for rather than
     *  proposed blind. */
    error?: string;
    /**
     * When it was made, and where it sits in the order.
     *
     * "Go back to the previous image" and "the last one" are the most ordinary
     * way to refer to a picture, and the listing had no order in it at all —
     * so the agent picked whichever entry it happened to land on, which was
     * usually the newest when the vendor meant the one before.
     */
    createdAt?: string;
    /** 1 is the oldest on the canvas; the highest number is the newest. */
    order?: number;
}

/** A change to an editing node already on the canvas. */
export interface CompiledEditUpdate {
    step: PlanOperation;
    editNodeId: string;
    /** New words and placement for a design node. */
    design?: DesignSpec;
    /** New shape for an expand node. */
    aspectRatio?: string;
    /** New areas for a retouch node, in source pixels. */
    retouch?: PlannedRegion[];
    /** New instruction for a modify node. */
    modifyPrompt?: string;
    /** Reference picture for a modify node — the thing to match, seen not described. */
    modifyReferenceUrl?: string;
}

/** An image-editing node chained onto a generation — upscale, expand, retouch. */
export interface CanvasEditInfo {
    nodeId: string;
    tool: string;
    /** The generation it edits. */
    sourceNodeId: string;
    hasOutput: boolean;
    /**
     * What a design node currently says.
     *
     * Without it, "move the text to the top" was answered by chaining a second
     * design onto the first one's output — text over text — because the agent
     * could see that an edit node existed but not what was in it.
     */
    design?: DesignSpec;
}

/** A step that edits a node already on the canvas rather than adding one. */
export interface CompiledModification {
    step: PlanOperation;
    targetNodeId: string;
    recipe: CsRecipe;
    product?: PlannerProduct;
    /** Only the inputs that actually change — the preview shows old → new. */
    patch: Record<string, any>;
    previous: Record<string, any>;
    coercion: CoercionReport;
}

/** A node the plan wants removed, with everything that goes down with it. */
export interface CompiledDeletion {
    step: PlanOperation;
    targetNodeId: string;
    /** Every node id removed, cascade included. */
    removedIds: string[];
    /** What the vendor loses, for the preview. */
    label: string;
    generationsLost: number;
}

/** A node the plan wants to run as it already stands. */
export interface CompiledGeneration {
    step: PlanOperation;
    targetNodeId: string;
    /** What will be produced, for the preview. */
    label: string;
    /** Required fields still empty; a node with any cannot run. */
    missing: string[];
    creditCost: number;
}

/** Swapping which product a node on the canvas is about. */
export interface CompiledProductSwap {
    step: PlanOperation;
    /** The product node whose product changes. */
    productNodeId: string;
    from?: string;
    product: PlannerProduct;
    /** How many recipe nodes hang off it — they all follow the swap. */
    affectedRecipes: number;
}

/** Something the plan wants done to a generated image. */
export interface CompiledAssetAction {
    step: PlanOperation;
    kind: 'retry' | 'approve' | 'reject' | 'edit';
    generationNodeId: string;
    assetUuid: string;
    /** What the vendor sees named in the preview. */
    label: string;
    /** add_edit_node only. */
    tool?: string;
    /**
     * Expand only — how far to push each edge out, in source pixels.
     *
     * The plan names a shape ("9:16"); the arithmetic happens here, because
     * only the canvas knows how big the image actually is. Undefined when the
     * plan asked for no particular shape, which leaves the vendor to drag the
     * edges themselves as they always have.
     */
    expand?: { extendTop: number; extendRight: number; extendBottom: number; extendLeft: number };
    /** Design only — the text the plan asked for, laid out by the canvas. */
    design?: DesignSpec;
    /**
     * Retouch only — the areas to fix, already in the source image's pixels.
     *
     * The plan names them as fractions of the frame because it has never seen
     * how big the picture is; the arithmetic happens here, where it is known.
     */
    retouch?: PlannedRegion[];
    /** Modify only — what the whole picture should become. */
    modifyPrompt?: string;
    /** Modify only — a picture the change should match. */
    modifyReferenceUrl?: string;
    /**
     * The id the canvas will give this edit node — minted here, not there.
     *
     * A continuation has to name the nodes whose images it waits for, and an
     * edit produces an image like anything else. While the id was invented at
     * apply time the plan could not name it, so "expand it, then put text on
     * it" armed a follow-up against an empty set and stopped after the expand.
     */
    editNodeId?: string;
}

/** One area of a picture a plan wants fixed, in source pixels. */
export interface PlannedRegion {
    x: number;
    y: number;
    width: number;
    height: number;
    prompt: string;
}

/**
 * Turn the fractions a plan speaks in into the pixels the editor works in.
 *
 * Clamped to the frame: a box a little over the edge is a rounding artefact
 * from a vision model, and cropping it is better than refusing the retouch.
 * A region with no area left after clamping is dropped — it would submit a job
 * that paints nothing.
 */
export function regionsToPixels(
    regions: Array<{ x: number; y: number; width: number; height: number; prompt: string }>,
    source: { width: number; height: number },
): PlannedRegion[] {
    if (!source?.width || !source?.height) return [];
    return regions
        .map((r) => {
            const x = Math.round(Math.min(Math.max(r.x, 0), 1) * source.width);
            const y = Math.round(Math.min(Math.max(r.y, 0), 1) * source.height);
            const width = Math.round(Math.min(r.width, 1 - r.x) * source.width);
            const height = Math.round(Math.min(r.height, 1 - r.y) * source.height);
            return { x, y, width, height, prompt: (r.prompt || '').trim() };
        })
        .filter((r) => r.width > 0 && r.height > 0 && r.prompt.length > 0);
}

/** Text a plan wants laid over a picture, and roughly where. */
export interface DesignSpec {
    headline: string;
    subheadline?: string;
    placement: 'top' | 'bottom' | 'left' | 'right' | 'center';
    textColor?: string;
    /** Where the vendor's own logo goes, if it belongs on this one. */
    logoPlacement?: 'top-left' | 'top-right' | 'bottom-left' | 'bottom-right';
    /**
     * Lay the copy out with one of the Design editor's templates instead of the
     * plain text block. The template decides placement, colours and logo
     * position; placement / textColor / logoPlacement are then ignored.
     */
    template?: DesignTemplateId;
    /** Small letter-spaced label above the headline ("New arrival"). */
    eyebrow?: string;
    /** Call-to-action button text ("Book a showroom visit"). */
    cta?: string;
    /** A spec chip / tag ("200 × 200 mm"). */
    spec?: string;
    /**
     * The artboard shape for a template design: "1:1" | "4:5" | "9:16" |
     * "16:9" | "2:3". Copied from the edit operation's aspectRatio; absent =
     * the picture's own shape.
     */
    aspectRatio?: string;
}

/** The Design editor's template ids (components/design-editor/templates.ts). */
export const DESIGN_TEMPLATE_IDS = [
    'new-arrival', 'collection', 'spotlight', 'sale', 'story-cta', 'before-after', 'callouts',
    'tip', 'quote', 'moodboard', 'event', 'editorial', 'end-card',
] as const;
export type DesignTemplateId = typeof DESIGN_TEMPLATE_IDS[number];

/** A design op's spec, with the op's aspect ratio folded in for template designs. */
export function designWithAspect(design: DesignSpec | undefined, aspectRatio?: string): DesignSpec | undefined {
    if (!design || !design.template || !aspectRatio || design.aspectRatio) return design;
    return { ...design, aspectRatio };
}

/**
 * Turn "this headline, along the bottom" into a real Fabric scene.
 *
 * The agent decides what the words are and which part of the picture is free
 * for them — it has looked at the image, which is the part that needs judgement.
 * Everything below is typography: margins, sizes and line spacing proportional
 * to the frame, so the same spec reads correctly on a square post and a tall
 * story without the plan knowing either size.
 *
 * A vendor can still open the editor and move any of it. This is a first draft
 * laid out for them, not a lock.
 */
/** What a design falls back to when the plan does not say — the vendor's brand. */
export interface DesignBrandDefaults {
    /** The brand typeface, if the kit names one. */
    fontFamily?: string;
    /** The first brand colour, for text the plan did not colour explicitly. */
    textColor?: string;
    /** Every brand colour — templates pick their accent from these. */
    colors?: string[];
    /** Which colour does which job, when the kit worked it out. */
    colorRoles?: { ink?: string; ground?: string; accent?: string; onAccent?: string };
    /** Set as a wordmark when there is no logo (dark templates). */
    brandName?: string;
    /** The brand kit's own logo; preferred over the account logo for templates. */
    logoUrl?: string;
}

export function designSpecToFabric(
    spec: DesignSpec,
    width: number,
    height: number,
    /** The vendor's logo, when they have one and the plan asked for it. */
    logoUrl?: string,
    /**
     * The brand kit's font and colour. The plan's own choice wins when it made
     * one; these fill the gaps. Before this the design rendered in a hardcoded
     * sans even when the kit named the vendor's typeface, which is the single
     * most visible way generated content failed to look like them.
     */
    brand?: DesignBrandDefaults,
): any {
    // A template design is laid out where it is first drawn (it needs fonts and
    // the picture). The scene carries the request; the headless renderer and the
    // editor both expand it into the template — see design-editor/template-scene.ts.
    if (spec.template && (DESIGN_TEMPLATE_IDS as readonly string[]).includes(spec.template)) {
        const clean = (v?: string) => (typeof v === 'string' && v.trim() ? v.trim() : undefined);
        return {
            version: '5.3.0',
            objects: [],
            csTemplate: {
                id: spec.template,
                headline: spec.headline,
                eyebrow: clean(spec.eyebrow),
                subheadline: clean(spec.subheadline),
                cta: clean(spec.cta),
                spec: clean(spec.spec),
                aspectRatio: clean(spec.aspectRatio),
                brand: {
                    fontFamily: brand?.fontFamily,
                    textColor: brand?.textColor,
                    colors: brand?.colors,
                    colorRoles: brand?.colorRoles,
                    brandName: brand?.brandName,
                    logoUrl: brand?.logoUrl || logoUrl,
                },
            },
            // The source's size, so a reader that does not know templates still
            // maps the (empty) layer list onto the picture.
            authoredWidth: width,
            sourceHeight: height,
        };
    }
    const margin = Math.round(Math.min(width, height) * 0.07);
    const isHex = (v?: string) => /^#[0-9a-fA-F]{3,8}$/.test(v || '');
    const fill = isHex(spec.textColor) ? spec.textColor
        : isHex(brand?.textColor) ? brand!.textColor
            : '#111827';
    // The brand family first, then the same stack as before so a font that
    // has not loaded still falls to a sans rather than the canvas serif.
    const fontFamily = brand?.fontFamily
        ? `"${brand.fontFamily.replace(/"/g, '')}", Inter, Helvetica, Arial, sans-serif`
        : 'Inter, Helvetica, Arial, sans-serif';
    const side = spec.placement === 'left' || spec.placement === 'right';
    // A side column gets less room, so its type comes down with it.
    const boxWidth = side ? Math.round(width * 0.42) : width - margin * 2;
    const headSize = Math.round((side ? width * 0.055 : width * 0.078));
    const subSize = Math.round(headSize * 0.42);
    const gap = Math.round(headSize * 0.35);

    // Textbox wraps, so height is an estimate: enough lines for the words at
    // this size, which is what decides where a bottom-anchored block starts.
    const perLine = Math.max(1, Math.floor(boxWidth / (headSize * 0.55)));
    const headLines = Math.max(1, Math.ceil(spec.headline.length / perLine));
    const headHeight = headLines * headSize * 1.18;
    const subHeight = spec.subheadline ? subSize * 1.4 + gap : 0;
    const blockHeight = headHeight + subHeight;

    let top: number;
    if (spec.placement === 'top') top = margin;
    else if (spec.placement === 'bottom') top = Math.round(height - margin - blockHeight);
    else top = Math.round((height - blockHeight) / 2);

    let left: number;
    let textAlign: string;
    if (spec.placement === 'right') { left = Math.round(width - margin - boxWidth); textAlign = 'right'; }
    else if (spec.placement === 'left') { left = margin; textAlign = 'left'; }
    else { left = margin; textAlign = 'center'; }

    // Light type laid straight onto a photograph loses its edges wherever the
    // picture is bright. A soft, close shadow keeps it legible without the
    // boxed-in look of a plate; dark type on a light area needs none.
    const lightText = (() => {
        const m = String(fill).match(/^#?([0-9a-f]{2})([0-9a-f]{2})([0-9a-f]{2})/i)
            || String(fill).match(/^#?([0-9a-f])([0-9a-f])([0-9a-f])$/i);
        if (!m) return false;
        const ch = (h: string) => parseInt(h.length === 1 ? h + h : h, 16) / 255;
        return 0.2126 * ch(m[1]) + 0.7152 * ch(m[2]) + 0.0722 * ch(m[3]) > 0.6;
    })();

    const textbox = (text: string, y: number, fontSize: number, fontWeight: number, lineHeight = 1.16) => ({
        ...(lightText ? {
            shadow: {
                color: 'rgba(0, 0, 0, 0.32)',
                blur: Math.round(fontSize * 0.3),
                offsetX: 0,
                offsetY: Math.max(1, Math.round(fontSize * 0.04)),
            },
        } : {}),
        lineHeight,
        type: 'textbox',
        version: '5.3.0',
        text,
        // Fabric rebuilds per-character styling from this on load and writes it
        // back on save. Left out, `stylesFromArray` hands back undefined, the
        // Textbox is built without it, and the first serialization — which the
        // editor does the moment it opens, to seed undo — dies reading index 0
        // of nothing. An empty array is "no character overrides".
        styles: [],
        left,
        top: Math.round(y),
        width: boxWidth,
        // A stack, not a single family. Canvas silently falls back to its
        // default when a font is not loaded, and that default is a serif — so
        // the same design rendered sans in the flattened picture and serif in
        // the editor, purely on whether Inter had arrived yet.
        fontFamily,
        fontSize,
        fontWeight,
        fill,
        textAlign,
        originX: 'left',
        originY: 'top',
        scaleX: 1,
        scaleY: 1,
        angle: 0,
        splitByGrapheme: false,
        editable: true,
    });

    // Display sizes want tighter leading than body copy.
    const objects: any[] = [textbox(spec.headline, top, headSize, 700, 1.08)];
    if (spec.subheadline) {
        objects.push(textbox(spec.subheadline, top + headHeight + gap, subSize, 500));
    }

    // The vendor's mark, in a corner, deliberately away from the copy. Sized as
    // a fraction of the frame like everything else, and dropped silently when
    // they have no logo — an empty box where a logo should be is worse than no
    // logo at all.
    if (spec.logoPlacement && logoUrl) {
        const logoW = Math.round(width * 0.16);
        // Keep the mark out of the copy, whatever corner was asked for.
        //
        // The prompt asks for the corner furthest from the headline and the
        // model does not always oblige: a top-placed headline with a top-right
        // logo put the mark straight through "The Weekend Edit". Which side it
        // sits on is a taste question and stays the agent's; which half of the
        // frame is free is arithmetic, and belongs here.
        const textAtTop = spec.placement === 'top';
        const textAtBottom = spec.placement === 'bottom';
        let atTop = spec.logoPlacement.startsWith('top');
        if (textAtTop && atTop) atTop = false;
        if (textAtBottom && !atTop) atTop = true;
        const atLeft = spec.logoPlacement.endsWith('left');
        objects.push({
            type: 'image',
            version: '5.3.0',
            src: logoUrl,
            left: margin,
            top: margin,
            originX: 'left',
            originY: 'top',
            angle: 0,
            crossOrigin: 'anonymous',
            // A logo's real proportions are only known once the bitmap loads,
            // so the render scales it and puts it in its corner then. Fixing a
            // width and height here would squash a wide wordmark into a square.
            fitLogo: { width: logoW, margin, frameW: width, frameH: height, atTop, atLeft },
        });
    }
    return { version: '5.3.0', objects };
}

/**
 * How far to extend each edge to turn an image into a different shape.
 *
 * Only ever grows: the point of expanding is to keep all of the picture the
 * vendor chose and paint more around it. The added space is split evenly across
 * the two opposing edges so the subject stays where it was in frame.
 */
export function expansionForAspect(
    source: { width: number; height: number },
    aspectRatio: string,
): { extendTop: number; extendRight: number; extendBottom: number; extendLeft: number } | null {
    const [w, h] = aspectRatio.split(':').map(Number);
    if (!w || !h || !source?.width || !source?.height) return null;

    const target = w / h;
    const current = source.width / source.height;
    // Within a pixel of the target already — expanding would add nothing but a
    // generation the vendor pays for.
    if (Math.abs(current - target) < 0.005) return null;

    if (current > target) {
        const grow = Math.round(source.width / target) - source.height;
        const half = Math.max(0, Math.round(grow / 2));
        return { extendTop: half, extendBottom: grow - half, extendLeft: 0, extendRight: 0 };
    }
    const grow = Math.round(source.height * target) - source.width;
    const half = Math.max(0, Math.round(grow / 2));
    return { extendLeft: half, extendRight: grow - half, extendTop: 0, extendBottom: 0 };
}

export interface DroppedStep {
    step: PlanOperation;
    reason: string;
}

export interface CompiledPlan {
    /** Product nodes to create, one per distinct product the plan touches. */
    productNodes: CanvasProductNode[];
    steps: CompiledStep[];
    /** Steps that edit existing nodes instead of adding new ones. */
    modifications: CompiledModification[];
    /** Nodes to remove, each with its cascade already resolved. */
    deletions: CompiledDeletion[];
    /** True when the plan clears the whole canvas before anything else. */
    clearsCanvas: boolean;
    /** How many nodes a clear would remove. Zero unless clearsCanvas. */
    clearedCount: number;
    /** Existing nodes to run. "Generate" is an operation, not a second node. */
    generations: CompiledGeneration[];
    /** Retries, accept/reject decisions and edits chained onto generated images. */
    assetActions: CompiledAssetAction[];
    /** Changes to editing nodes already on the canvas. */
    editUpdates: CompiledEditUpdate[];
    /** Product substitutions on existing nodes. */
    productSwaps: CompiledProductSwap[];
    /** Steps that could not be compiled at all, with why. */
    dropped: DroppedStep[];
    /** Every node to insert, in creation order. */
    nodes: CanvasNode[];
    /** Video stories to write and shoot (see CompiledVideoStory). */
    videoStories?: CompiledVideoStory[];
    /**
     * What the agent wants to be asked once this plan's images have finished.
     *
     * A run ends at the proposal, so a request spanning a generation — "make a
     * week of posts, then pick the best three" — had no second half. This arms
     * one: the panel waits for the generations below, then asks again on the
     * same thread with the results in view.
     */
    continuation?: string;
}

/**
 * The nodes an applied plan will actually run.
 *
 * Shared because two places need the same answer and disagreeing is a real
 * bug: the canvas fires these, and the agent panel waits on exactly them to
 * decide a continuation is due. A list that drifts leaves a follow-up waiting
 * for an image nobody is making.
 *
 * A node still missing required fields is left out rather than failed — the
 * card already says why it cannot run, and queueing it from a plan would go
 * around the rule the Generate button enforces.
 */
/**
 * Is the only thing this node lacks a frame the plan itself will capture?
 *
 * A node with the 3D studio on and a named angle has no `snapshotUrl` when the
 * plan is written, because the studio has not posed itself yet. Counting that
 * as "cannot run" meant a vendor asking for a low-angle shot *and* the picture
 * was offered plain Apply, the studio dutifully captured the frame, and then
 * nothing ran — the one step they actually wanted.
 */
export function awaitingOwnCapture(inputs: Record<string, any> | undefined, missing: string[]): boolean {
    // Deduplicated: a recipe that declares the frame required *and* has it added
    // by the 3D switch reports it twice, and counting the entries rather than
    // the distinct keys quietly turned this whole rule off.
    const gaps = new Set(missing);
    if (!inputs || gaps.size !== 1 || !gaps.has('snapshotUrl')) return false;
    return !!inputs.cameraAngle
        && (inputs.use3dCamera === true || inputs.use3dCamera === 'true');
}

export function nodesToGenerate(compiled: CompiledPlan): string[] {
    return [...new Set([
        ...compiled.steps.filter((s) => s.missing.length === 0).map((s) => s.node.id),
        ...compiled.modifications
            .filter((m) => m.coercion.missing.length === 0)
            .map((m) => m.targetNodeId),
        ...compiled.generations
            .filter((g) => g.missing.length === 0)
            .map((g) => g.targetNodeId),
    ])];
}

/**
 * Every node a plan will eventually produce an image from.
 *
 * Wider than `nodesToGenerate`, and deliberately: a node that has to pose the
 * 3D studio first counts as missing `snapshotUrl` at compile time, so it is not
 * in the fire-list — the studio captures the frame and generates it afterwards.
 * A continuation armed off the fire-list therefore waited on an empty set and
 * was never armed at all, which is why "shoot it, then make it a 9:16 story"
 * stopped dead at the render every single time.
 */
export function nodesAwaited(compiled: CompiledPlan): string[] {
    return [...new Set([
        ...nodesToGenerate(compiled),
        ...compiled.steps
            .filter((s) => awaitingOwnCapture(s.node.inputs, s.missing))
            .map((s) => s.node.id),
        ...compiled.modifications
            .filter((m) => awaitingOwnCapture(
                { ...m.previous, ...m.patch }, m.coercion.missing,
            ))
            .map((m) => m.targetNodeId),
        // Edits that run themselves produce an image too. Retouch and modify are
        // left out on purpose: the vendor has to draw the region or type the
        // instruction first, so waiting on one could wait forever.
        ...compiled.assetActions
            .filter((a) => a.kind === 'edit' && a.editNodeId && selfRunningEdit(a))
            .map((a) => a.editNodeId!),
        // Rewording or recolouring an existing design re-renders it and runs
        // again — a new picture, not a new setting — so it is promised work and
        // the canvas invariant should hold it to that.
        ...compiled.editUpdates.map((u) => u.editNodeId),
    ])];
}

/** Does this edit have everything it needs to run the moment it lands? */
export function selfRunningEdit(
    action: Pick<CompiledAssetAction, 'tool' | 'expand' | 'design' | 'retouch' | 'modifyPrompt'>,
): boolean {
    return !!action.expand
        || !!action.design
        || !!action.retouch?.length
        || !!action.modifyPrompt
        || action.tool === 'upscale';
}

/**
 * Editing tools a plan may ask for.
 *
 * Narrower than `EditImageTool`, which still carries `graphics` so canvases
 * that already have one keep working. Design replaced it, and the two are easy
 * to confuse from the name alone — which is exactly how a request for a title
 * ended up on the retired one.
 */
const PLANNABLE_EDIT_TOOLS = new Set(['upscale', 'expand', 'retouch', 'modify', 'design']);

/** Horizontal distance between a product column and its recipes. */
const COLUMN_GAP = 420;
/** Vertical distance between sibling nodes in a column. */
const ROW_GAP = 380;

/**
 * Turn an agent's plan into canvas nodes.
 *
 * The plan deliberately carries no ids or coordinates — the model names
 * products and steps, and placement happens here, where the canvas conventions
 * live. Layout is a simple tier: products in one column, the recipes that
 * depend on them in the next, chained recipes one column further right. The
 * page then offsets the whole block to a free area, so this only has to be
 * internally consistent.
 *
 * Every step's inputs go through the same coercion the planner's validator
 * uses, so a value the model invented is snapped or dropped here rather than
 * reaching a generation. A step whose product or recipe cannot be resolved is
 * dropped with a reason rather than silently skipped — the vendor is told what
 * did not survive.
 */
export function compilePlan(
    plan: ContentPlan,
    products: Map<string, PlannerProduct>,
    recipes: CsRecipe[],
    origin: { x: number; y: number } = { x: 0, y: 0 },
    existingNodes: Map<string, ExistingNode> = new Map(),
    /** The live graph — needed to resolve what a deletion takes with it. */
    canvasNodes: CanvasNode[] = [],
): CompiledPlan {
    const recipeBySlug = new Map(recipes.map((r) => [r.slug, r]));

    const productNodes: CanvasProductNode[] = [];
    const productNodeByUuid = new Map<string, CanvasProductNode>();
    const compiled: CompiledStep[] = [];
    const modifications: CompiledModification[] = [];
    const deletions: CompiledDeletion[] = [];
    const generations: CompiledGeneration[] = [];
    const assetActions: CompiledAssetAction[] = [];
    const editUpdates: CompiledEditUpdate[] = [];
    const productSwaps: CompiledProductSwap[] = [];
    const dropped: DroppedStep[] = [];
    let clearsCanvas = false;
    /** Plan step id → the recipe node it produced, for resolving chains. */
    const nodeByStepId = new Map<string, CanvasRecipeNode>();

    const operations = plan?.operations || [];
    // Only additions take part in layout — a product column is built from
    // them — but edits still have to run through the same coercion, so both
    // kinds go through the main loop.
    const additions = operations.filter((o) => o.op === 'add_recipe');
    const steps = operations.filter(
        (o) => o.op === 'add_recipe' || o.op === 'update_recipe',
    );

    // Destructive operations first — they say what the vendor is about to lose,
    // which is what the preview leads with.
    for (const operation of operations) {
        if (operation.op === 'clear_canvas') {
            clearsCanvas = true;
            continue;
        }
        if (operation.op !== 'delete_node') continue;

        const targetId = operation.targetNodeId!;
        const target = canvasNodes.find((n) => n.id === targetId);
        if (!target) {
            dropped.push({ step: operation, reason: 'That node is no longer on the canvas' });
            continue;
        }

        const removedIds = [...collectNodeAndDescendants(canvasNodes, targetId)];

        // A product node exists to feed recipes. Removing its last one leaves a
        // product sitting alone on the canvas connected to nothing, which the
        // vendor reads as leftover mess — they asked for the duplicate to go,
        // not for half of it to go.
        if (target.kind === 'recipe') {
            const parentId = (target as CanvasRecipeNode).productNodeId;
            const parent = canvasNodes.find((n) => n.id === parentId);
            if (parent?.kind === 'product') {
                const stillUsed = canvasNodes.some(
                    (n) => n.kind === 'recipe'
                        && (n as CanvasRecipeNode).productNodeId === parentId
                        && !removedIds.includes(n.id),
                );
                if (!stillUsed) removedIds.push(parentId!);
            }
        }

        const removed = canvasNodes.filter((n) => removedIds.includes(n.id));
        const generationsLost = removed.filter((n) => n.kind === 'generation').length;

        const label = target.kind === 'product'
            ? (target as CanvasProductNode).products?.[0]?.name || 'Product'
            : target.kind === 'recipe'
            ? recipeBySlug.get((target as CanvasRecipeNode).recipeSlug)?.name
                || (target as CanvasRecipeNode).recipeSlug
            : target.kind;

        deletions.push({ step: operation, targetNodeId: targetId, removedIds, label, generationsLost });
    }

    // Nodes to run as they stand. "Generate" used to have no representation, so
    // the agent expressed it as a second copy of the node — or as an edit that
    // changed nothing, which the compiler then rejected.
    for (const operation of operations) {
        if (operation.op !== 'generate_node') continue;

        const targetId = operation.targetNodeId!;
        const existing = existingNodes.get(targetId);
        if (!existing) {
            dropped.push({ step: operation, reason: 'That node is no longer on the canvas' });
            continue;
        }

        const recipe = recipeBySlug.get(existing.recipeSlug);
        const shown = surfaceShotName(
            existing.recipeSlug, existing.inputs, existing.product?.visualizationType,
        ) || recipe?.name || existing.recipeSlug;
        generations.push({
            step: operation,
            targetNodeId: targetId,
            label: existing.product?.name ? `${shown} · ${existing.product.name}` : shown,
            // Reported rather than blocking: the vendor sees why it cannot run
            // and the agent is told to fill them in the same plan.
            missing: existing.missing || [],
            creditCost: recipe?.creditCost || 0,
        });
    }

    // Things done to a generated image rather than to a recipe node. These name
    // a generation, so they are resolved against the live graph — the vendor
    // pointing at a picture is pointing at one of these, not at the recipe that
    // produced five of them.
    // Changes to an editing node that is already there — moving a headline is a
    // change to the design that wrote it, not another design over its output.
    for (const operation of operations) {
        if (operation.op !== 'update_edit_node' || !operation.editNodeId) continue;
        const node = canvasNodes.find(
            (n) => n.id === operation.editNodeId && n.kind === 'edit-image',
        ) as CanvasEditImageNode | undefined;
        if (!node) {
            dropped.push({ step: operation, reason: 'That editing node is no longer on the canvas' });
            continue;
        }
        // The picture this node edits, for turning fractions into pixels.
        const source = canvasNodes.find(
            (n) => n.kind === 'generation'
                && (n as CanvasGenerationNode).asset?.uuid === node.sourceAssetUuid,
        ) as CanvasGenerationNode | undefined;
        const dims = source?.asset?.dimensions;

        editUpdates.push({
            step: operation,
            editNodeId: operation.editNodeId,
            design: designWithAspect(operation.design, operation.aspectRatio),
            aspectRatio: operation.aspectRatio,
            retouch: operation.regions?.length && dims
                ? regionsToPixels(operation.regions, dims)
                : undefined,
            modifyPrompt: operation.prompt?.trim() || undefined,
            modifyReferenceUrl: operation.referenceImageUrl?.trim() || undefined,
        });
    }

    for (const operation of operations) {
        const kind = operation.op === 'retry_generation' ? 'retry'
            : operation.op === 'approve_generation'
                ? (operation.approved === false ? 'reject' : 'approve')
                : operation.op === 'add_edit_node' ? 'edit'
                    : undefined;
        if (!kind) continue;

        const genId = operation.generationNodeId!;
        const node = canvasNodes.find((n) => n.id === genId && n.kind === 'generation') as
            CanvasGenerationNode | undefined;
        if (!node) {
            dropped.push({
                step: operation,
                reason: 'That image is no longer on the canvas',
            });
            continue;
        }

        if (kind === 'retry' && node.asset?.status !== 'failed') {
            // Retrying a good image would delete it and spend credits redoing
            // work that already landed.
            dropped.push({ step: operation, reason: 'That image did not fail, so there is nothing to retry' });
            continue;
        }
        if (kind === 'edit' && node.asset?.status !== 'ready') {
            dropped.push({ step: operation, reason: 'That image has not finished, so it cannot be edited yet' });
            continue;
        }
        if (kind === 'edit' && operation.tool && !PLANNABLE_EDIT_TOOLS.has(operation.tool)) {
            // A retired tool reaching here still builds a working node, which is
            // the problem: asked for a title, the agent chose the retired
            // "graphics" editor and the vendor got a tool nothing else on the
            // canvas offers any more. Say so rather than quietly building it.
            dropped.push({
                step: operation,
                reason: `"${operation.tool}" is not an editing tool any more — Design does text and layout now`,
            });
            continue;
        }

        const parent = canvasNodes.find((n) => n.id === node.recipeNodeId);
        const parentSlug = parent?.kind === 'recipe'
            ? (parent as CanvasRecipeNode).recipeSlug : undefined;

        assetActions.push({
            step: operation,
            kind,
            generationNodeId: genId,
            assetUuid: node.asset?.uuid,
            label: recipeBySlug.get(parentSlug || '')?.name || 'Image',
            tool: operation.tool,
            // "Make it 9:16" turned into pixels here, where the source size is
            // known. The plan cannot do this arithmetic — it has never seen how
            // big the image is — and a guess would crop or letterbox it.
            expand: (operation.tool === 'expand' && operation.aspectRatio && node.asset?.dimensions)
                ? expansionForAspect(node.asset.dimensions, operation.aspectRatio) || undefined
                : undefined,
            // Passed through as the spec, not a scene: laying it out needs the
            // picture's real size and the vendor's logo, and the canvas is
            // where both of those live.
            design: operation.tool === 'design' ? designWithAspect(operation.design, operation.aspectRatio) : undefined,
            // Same reasoning as expand: the plan says where in fractions of the
            // frame, and the pixels are worked out here where the size is known.
            retouch: (operation.tool === 'retouch' && operation.regions?.length
                && node.asset?.dimensions)
                ? regionsToPixels(operation.regions, node.asset.dimensions)
                : undefined,
            modifyReferenceUrl: operation.tool === 'modify'
                ? operation.referenceImageUrl?.trim() || undefined
                : undefined,
            modifyPrompt: operation.tool === 'modify' && operation.prompt?.trim()
                ? operation.prompt.trim()
                : undefined,
            editNodeId: kind === 'edit' ? `edit-${uuidv4().slice(0, 8)}` : undefined,
        });
    }

    /**
     * Product nodes already on the canvas, by the product they hold.
     *
     * A second shot of the same sofa belongs under the sofa that is already
     * there. Without this every plan brought its own copy of the product, so
     * asking for one more angle left the vendor with two identical product
     * nodes and their work split across both — which happened on nearly every
     * follow-up turn.
     */
    // Only nodes that survive this plan. A plan that clears the canvas and
    // starts again is the common "revert and redo", and reusing a product node
    // it is in the middle of deleting left three recipes pointing at something
    // that no longer existed — no product, no source, nothing to run.
    const removedByPlan = new Set(deletions.flatMap((d) => d.removedIds));
    const existingProductNodes = new Map<string, CanvasProductNode>();
    if (!clearsCanvas) {
        for (const node of canvasNodes) {
            if (node.kind !== 'product' || removedByPlan.has(node.id)) continue;
            const held = (node as CanvasProductNode).products?.[0]?.uuid;
            if (held && !existingProductNodes.has(held)) {
                existingProductNodes.set(held, node as CanvasProductNode);
            }
        }
    }

    // Product column: one node per distinct product, in first-mention order so
    // the layout follows the plan's own narrative.
    for (const step of additions) {
        const uuid = step.productUuid;
        if (!uuid || productNodeByUuid.has(uuid)) continue;
        // Reuse rather than duplicate. Registered without being added to
        // `productNodes`, so it is not re-inserted or laid out again — the
        // recipes below simply hang off the node that is already there.
        const already = existingProductNodes.get(uuid);
        if (already) { productNodeByUuid.set(uuid, already); continue; }
        const product = products.get(uuid);
        if (!product) continue;

        const node: CanvasProductNode = {
            kind: 'product',
            id: uuidv4(),
            x: origin.x,
            y: origin.y + productNodes.length * ROW_GAP,
            products: [product],
        };
        productNodes.push(node);
        productNodeByUuid.set(uuid, node);
    }

    // Recipe columns. Depth is how far a step sits down a chain, which decides
    // its column; rows fill top-down within each column.
    const depthByStepId = new Map<string, number>();
    const rowsByDepth = new Map<number, number>();

    for (const step of steps) {
        const recipe = recipeBySlug.get(step.recipeSlug!);

        // A modify step edits a node that already exists — it produces a patch
        // rather than a node, and never takes part in the layout.
        //
        // Its recipe comes from the target node, not the step: the node on the
        // canvas is what's being edited, so its slug is the authoritative one.
        // Requiring the step to repeat it dropped every edit the agent produced,
        // because "change this node's background" names a node, not a recipe.
        if (step.op === 'update_recipe' && step.targetNodeId) {
            const existing = existingNodes.get(step.targetNodeId);
            if (!existing) {
                dropped.push({
                    step,
                    reason: 'The node it wanted to change is no longer on the canvas',
                });
                continue;
            }

            const target = recipeBySlug.get(existing.recipeSlug) || recipe;
            if (!target) {
                dropped.push({ step, reason: `Unknown recipe "${existing.recipeSlug}"` });
                continue;
            }
            const coercion = coerceRecipeInputs(
                {
                    recipe: target,
                    inputs: existing.inputs,
                    productVisualizationType: existing.product?.visualizationType,
                    productApplicationTypes: existing.product?.applicationTypes,
                },
                step.inputs || {},
            );

            // Keep only what actually changes, so the preview can show the
            // edit rather than restating the node's whole configuration.
            //
            // Restricted to keys the plan actually proposed. Coercion fills
            // required gaps from declared defaults, which is right when
            // building a node and wrong when patching one: asked to change a
            // scene to a bathroom, it also reset an untouched aspect ratio from
            // 16:9 to the recipe's 1:1 default, and that was the only change
            // that survived to the card.
            // "Replace the product with that one" arrives as productUuid on an
            // update, which is the natural shape and was silently ignored: only
            // `inputs` was ever applied, so the plan reported a swap the canvas
            // never made. The product lives on the source node, so that is what
            // changes — every recipe hanging off it follows.
            if (step.productUuid && step.productUuid !== existing.product?.uuid) {
                const replacement = products.get(step.productUuid);
                const sourceNode = canvasNodes.find(
                    (n) => n.kind === 'recipe' && n.id === step.targetNodeId,
                ) as CanvasRecipeNode | undefined;
                const productNodeId = sourceNode?.productNodeId;

                if (!replacement) {
                    dropped.push({ step, reason: 'That product is not in this catalog' });
                } else if (!productNodeId) {
                    dropped.push({ step, reason: 'That node has no product to replace' });
                } else {
                    productSwaps.push({
                        step,
                        productNodeId,
                        from: existing.product?.name,
                        product: replacement,
                        affectedRecipes: canvasNodes.filter(
                            (n) => n.kind === 'recipe'
                                && (n as CanvasRecipeNode).productNodeId === productNodeId,
                        ).length,
                    });
                }
            }

            const proposed = new Set(Object.keys(step.inputs || {}));
            const patch: Record<string, any> = {};
            for (const [key, value] of Object.entries(coercion.inputs)) {
                if (!proposed.has(key)) continue;
                if (existing.inputs[key] !== value) patch[key] = value;
            }

            // Everything it asked for was discarded — usually a field that
            // doesn't exist in the node's current mode. Presenting that as an
            // edit shows a confident summary above an empty or unrelated diff.
            if (Object.keys(patch).length === 0) {
                // A pure product swap is a real change even with no input edits.
                if (productSwaps.some((sw) => sw.step === step)) continue;
                const lost = coercion.dropped.map((d) => d.key).join(', ');
                dropped.push({
                    step,
                    reason: lost
                        ? `${lost} cannot be set on this node as it is currently configured`
                        : 'Nothing about the node would change',
                });
                continue;
            }

            modifications.push({
                step,
                targetNodeId: step.targetNodeId,
                recipe: target,
                product: existing.product,
                patch,
                previous: existing.inputs,
                coercion,
            });
            continue;
        }

        // Everything past here builds a new node, which does need a named recipe.
        if (!recipe) {
            dropped.push({ step, reason: `Unknown recipe "${step.recipeSlug}"` });
            continue;
        }

        // Resolve what this step hangs off: a product, or an earlier step.
        let sourceNodeId: string | undefined;
        let product: PlannerProduct | undefined;
        let depth: number;

        if (step.sourceOpId) {
            const parent = nodeByStepId.get(step.sourceOpId);
            if (!parent) {
                dropped.push({
                    step,
                    reason: `Depends on "${step.sourceOpId}", which did not compile`,
                });
                continue;
            }
            sourceNodeId = parent.id;
            depth = (depthByStepId.get(step.sourceOpId) ?? 0) + 1;
            // A chained step inherits the upstream product, so validation still
            // knows whether it is working with a surface or a piece of furniture.
            const parentStep = additions.find((s) => s.id === step.sourceOpId);
            if (parentStep?.productUuid) product = products.get(parentStep.productUuid);
        } else if (step.productUuid) {
            const productNode = productNodeByUuid.get(step.productUuid);
            product = products.get(step.productUuid);
            if (!productNode || !product) {
                dropped.push({ step, reason: `Product "${step.productUuid}" is not in this catalog` });
                continue;
            }
            sourceNodeId = productNode.id;
            depth = 1;
        } else {
            dropped.push({ step, reason: 'Operation has neither a product nor a source' });
            continue;
        }

        // For a surface product this recipe is two different shots, chosen by a
        // control key that defaults to Room Scene. A plan that omits it lands on
        // a node where every backdrop and framing field it set is hidden — the
        // change applies and the vendor sees nothing. The agent is asked to be
        // explicit; when it isn't, a product shot is the truer reading of
        // "photograph this tile" than a room scene.
        const inputs = { ...(step.inputs || {}) };
        if (recipe.slug === 'product-closeup'
            && product?.visualizationType === 'surface'
            && !inputs.surfaceMode) {
            inputs.surfaceMode = 'single';
        }
        // Single Piece has no 3D studio. A plan that switched it on — and they
        // do — produced a node waiting forever for a capture nobody could take,
        // shown as "Needs a detail before it can run" with nothing on the card
        // to fill in. Dropped here rather than only in the node, because the
        // plan card computes what is missing before any node mounts.
        // Same for a detail shot: it is made from the product's own photograph,
        // and a camera angle left on it armed a capture that never came.
        if (recipe.slug === 'product-closeup' && inputs.shot === 'detail' && inputs.detailCamera !== 'studio') {
            delete inputs.use3dCamera;
            delete inputs.cameraView;
            delete inputs.cameraAngle;
            delete inputs.snapshotUrl;
        }
        if (recipe.slug === 'product-closeup'
            && product?.visualizationType === 'surface'
            && inputs.surfaceMode === 'single') {
            delete inputs.use3dCamera;
            delete inputs.cameraView;
            delete inputs.cameraAngle;
            delete inputs.snapshotUrl;
        }

        // Force the model's inputs onto what the recipe actually accepts.
        const coercion = coerceRecipeInputs(
            {
                recipe,
                inputs: {},
                productVisualizationType: product?.visualizationType,
                productApplicationTypes: product?.applicationTypes,
            },
            inputs,
        );

        const row = rowsByDepth.get(depth) ?? 0;
        rowsByDepth.set(depth, row + 1);

        const node: CanvasRecipeNode = {
            kind: 'recipe',
            id: uuidv4(),
            x: origin.x + depth * COLUMN_GAP,
            y: origin.y + row * ROW_GAP,
            productNodeId: sourceNodeId!,
            recipeSlug: step.recipeSlug,
            inputs: coercion.inputs,
            generationNodeIds: [],
            generating: false,
        };

        nodeByStepId.set(step.id!, node);
        depthByStepId.set(step.id!, depth);
        // Folded by default — a plan's nodes are a means to the pictures, and
        // four open cards of settings hide them.
        node.collapsed = true;
        compiled.push({ step, node, recipe, product, coercion, missing: coercion.missing });
    }

    // Video stories: a brief and the products it is about. Kept even when a
    // product cannot be resolved here — the storyboard is written server-side,
    // which reads the catalog itself — but a story with no brief is nothing.
    const videoStories: CompiledVideoStory[] = [];
    for (const operation of operations) {
        if (operation.op !== 'add_video_story') continue;
        const brief = String(operation.brief || operation.prompt || '').trim();
        const productUuids = Array.from(new Set([
            ...(operation.productUuids || []),
            ...(operation.productUuid ? [operation.productUuid] : []),
        ].filter(Boolean)));
        if (!brief && productUuids.length === 0) {
            dropped.push({ step: operation, reason: 'A video story needs a brief or a product' });
            continue;
        }
        const seconds = Number(operation.durationSec);
        videoStories.push({
            step: operation,
            brief,
            productUuids,
            productNames: productUuids.map((u) => products.get(u)?.name).filter(Boolean) as string[],
            aspect: ['9:16', '1:1', '4:5', '16:9'].includes(String(operation.aspectRatio))
                ? String(operation.aspectRatio)
                : '9:16',
            durationSec: Number.isFinite(seconds) && seconds > 0 ? Math.min(60, Math.max(6, Math.round(seconds))) : 15,
        });
    }

    return {
        videoStories,
        productNodes,
        steps: compiled,
        modifications,
        deletions,
        clearsCanvas,
        clearedCount: clearsCanvas ? canvasNodes.length : 0,
        generations,
        assetActions,
        editUpdates,
        productSwaps,
        dropped,
        nodes: [...productNodes, ...compiled.map((c) => c.node)],
        continuation: plan.continuation?.instruction?.trim() || undefined,
    };
}

/** True when a plan has nothing to apply — neither new nodes nor edits. */
export function isEmptyPlan(compiled: CompiledPlan): boolean {
    return compiled.steps.length === 0
        && compiled.modifications.length === 0
        && compiled.deletions.length === 0
        && compiled.generations.length === 0
        && compiled.assetActions.length === 0
        // Changing a design that is already on the canvas is a whole plan on its
        // own, and the commonest one there is: "write the title in white
        // instead". Missing from this list, the compiler built the change
        // correctly and it was then thrown away as nothing — the vendor was told
        // none of it could be built from their catalog, which was not true and
        // not even about their catalog.
        && compiled.editUpdates.length === 0
        && compiled.productSwaps.length === 0
        // A video story is a whole plan on its own: its nodes only exist once
        // the storyboard is written, so it must not be thrown away as nothing.
        && !(compiled.videoStories?.length)
        && !compiled.clearsCanvas;
}

/** Bounding size of a compiled plan, so the page can find room for the block. */
export function compiledPlanExtent(
    compiled: CompiledPlan,
): { width: number; height: number } {
    if (compiled.nodes.length === 0) return { width: 0, height: 0 };
    const xs = compiled.nodes.map((n) => n.x);
    const ys = compiled.nodes.map((n) => n.y);
    return {
        width: Math.max(...xs) - Math.min(...xs) + COLUMN_GAP,
        height: Math.max(...ys) - Math.min(...ys) + ROW_GAP,
    };
}

/** Shift every node by a delta — used to drop the block into free canvas space. */
export function offsetCompiledPlan(compiled: CompiledPlan, dx: number, dy: number): void {
    for (const node of compiled.nodes) {
        node.x += dx;
        node.y += dy;
    }
}
